import type { PropsWithChildren } from 'react';
import React from 'react';

import { ThemeProvider as ThemeProviderTomato } from '@change/ds-core-ui';

import { ThemeProvider as ThemeProviderSunrise } from '@change/ds-core-ui/next';

import { Box } from '@change-corgi/design-system-legacy/layout';

import { useSessionConfiguration } from 'src/app/shared/hooks/sessionConfiguration';

import { Footer as FooterTomato } from './components/Footer';
import { Footer as FooterSunrise } from './components/Footer/v2';
import { Header as HeaderTomato } from './components/Header';
import { Header as HeaderSunrise } from './components/Header/v2';
import type { DefaultLayoutProps } from './shared/types';

export const Layout = ({
	hideHeader,
	hideFooter,
	suppressHydrationWarning,
	children,
}: PropsWithChildren<DefaultLayoutProps> & { suppressHydrationWarning?: boolean }): React.JSX.Element | null => {
	const { featureConfigs } = useSessionConfiguration();

	if (hideFooter && hideHeader) {
		return (
			<Box
				// needed so that App can focus fallback on it
				data-maincontent-principal
				sx={{ height: '100%' }}
				role="main"
				suppressHydrationWarning={suppressHydrationWarning}
			>
				{children}
			</Box>
		);
	}

	return (
		<>
			{/* https://philipwalton.com/articles/normalizing-cross-browser-flexbox-bugs/ */}
			<Box suppressHydrationWarning={suppressHydrationWarning}>
				{!hideHeader &&
					(featureConfigs.sunriseNavEnabled ? (
						<ThemeProviderSunrise>
							<HeaderSunrise className="shrink-0" />
						</ThemeProviderSunrise>
					) : (
						<ThemeProviderTomato>
							<HeaderTomato className="shrink-0" />
						</ThemeProviderTomato>
					))}
				<Box
					// needed so that App can focus fallback on it
					data-maincontent-principal
					// this is to avoid blank space after the footer
					// using flex:1 on the body currently doesn't work because of the banners being outside of the layout
					// and that can sometimes result in double scrollbars on desktop
					// 300px come from manually measuring the height of the footer and header
					// (not great but better than nothing for now)
					sx={{ minHeight: hideFooter ? undefined : [null, 'calc(100vh - 300px)'] }}
					data-qa="page-content"
					role="main"
				>
					{children}
				</Box>
				{!hideFooter &&
					(featureConfigs.sunriseNavEnabled ? (
						<ThemeProviderSunrise>
							<FooterSunrise className="mb-8 shrink-0" />
						</ThemeProviderSunrise>
					) : (
						<ThemeProviderTomato>
							<FooterTomato className="mb-8 shrink-0" />
						</ThemeProviderTomato>
					))}
			</Box>
		</>
	);
};

export const HEADER_HEIGHT = 60;
